import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { apiUrl } from "../../../config/config";
import "./AttendanceView.css";

const RANGES = [
  { key: "today", label: "Today" },
  { key: "week", label: "This Week" },
  { key: "month", label: "This Month" },
  { key: "custom", label: "Custom" },
];

// Returns YYYY-MM-DD for a given Date in local time. Used to seed the
// custom-range inputs and to build the API query string.
const isoDay = (d) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const fmtTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const fmtDuration = (minutes) => {
  if (!Number.isFinite(minutes) || minutes <= 0) return "0h 0m";
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return `${h}h ${m}m`;
};

// Avg/median seconds-per-file display. Short, expressive: "42s", "2m 10s",
// "—" when there aren't enough samples to compute.
const fmtVerifySpeed = (seconds) => {
  if (!Number.isFinite(seconds) || seconds <= 0) return "—";
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
};

// Verified-ECG duration is often much larger than session time (one
// verifier can review days of recordings in an hour). Format short values
// in hours+minutes and longer values in days for at-a-glance readability.
const fmtVerifiedDuration = (minutes) => {
  if (!Number.isFinite(minutes) || minutes <= 0) return "0h";
  if (minutes >= 24 * 60) {
    const days = minutes / (24 * 60);
    return days >= 10 ? `${Math.round(days)}d` : `${days.toFixed(1)}d`;
  }
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString([], {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

// CSV-escape: wrap cells containing commas/quotes/newlines in quotes and
// double internal quotes per RFC 4180.
const csvCell = (v) => {
  if (v === null || v === undefined) return "";
  const s = String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const downloadCsv = (rows, filename) => {
  const csv = rows.map((r) => r.map(csvCell).join(",")).join("\r\n");
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// Activity tier classes — used by both the bar fill (single-verifier daily
// table) and the plain-text % (team table) so the color scheme is consistent.
//   0–29  : red    — well below target
//   30–59 : orange — needs attention
//   60–79 : amber  — acceptable
//   80–100: green  — strong
const activityTierClass = (value) => {
  const v = Math.max(0, Math.min(100, Number(value) || 0));
  if (v < 30) return "att-activity--low";
  if (v < 60) return "att-activity--med-low";
  if (v < 80) return "att-activity--med-high";
  return "att-activity--high";
};

const Kpi = ({ label, value, sub, tone }) => (
  <div className={"att-kpi" + (tone ? ` att-kpi--${tone}` : "")}>
    <div className="att-kpi__value">{value}</div>
    <div className="att-kpi__label">{label}</div>
    {sub ? <div className="att-kpi__sub">{sub}</div> : null}
  </div>
);

const ActivityBar = ({ value }) => {
  const v = Math.min(100, Math.max(0, Number(value) || 0));
  const tier = activityTierClass(v);
  return (
    <div className={"att-activity " + tier}>
      <div className="att-activity__track">
        <div
          className="att-activity__fill"
          style={{ width: `${v}%` }}
          aria-hidden
        />
      </div>
      <span className="att-activity__num">{v}%</span>
    </div>
  );
};

// Plain-text activity % with tier color — used in the team table where a
// full bar widget would be too wide.
const ActivityText = ({ value }) => {
  const v = Math.max(0, Math.min(100, Math.round(Number(value) || 0)));
  return (
    <span className={"att-activity-text " + activityTierClass(v)}>{v}%</span>
  );
};

// Generic mini-bar chart. Pass valueKey to pick which field to plot,
// fixedMax to set an explicit ceiling (e.g. 100 for activity %), and
// fillVariant to swap colour. Keeping one component avoids drift between
// the two charts and makes adding a third (e.g. break time) trivial.
const MiniBars = ({
  days,
  valueKey,
  fixedMax,
  fillVariant,
  formatValue,
  ariaLabel,
}) => {
  const ordered = [...days].sort(
    (a, b) => new Date(a.DayDate) - new Date(b.DayDate)
  );
  const ceiling =
    fixedMax ||
    ordered.reduce((m, d) => Math.max(m, d[valueKey] || 0), 0) ||
    1;
  return (
    <div className="att-bars" role="img" aria-label={ariaLabel}>
      {ordered.map((d) => {
        const v = d[valueKey] || 0;
        const h = Math.max(2, Math.round((v / ceiling) * 100));
        return (
          <div
            key={d.DayDate}
            className="att-bar"
            title={`${fmtDate(d.DayDate)} — ${formatValue(v)}`}
          >
            <div
              className={
                "att-bar__fill" +
                (fillVariant ? ` att-bar__fill--${fillVariant}` : "")
              }
              style={{ height: `${h}%` }}
            />
          </div>
        );
      })}
    </div>
  );
};

const AttendanceView = ({ selfOnly = false } = {}) => {
  const [range, setRange] = useState("today");
  // When `selfOnly` is true (e.g. embedded in a verifier's profile page),
  // we lock the view to "me" and never show the All-Verifiers toggle.
  const [scope, setScope] = useState("me"); // "me" | "team"
  // Custom date range (YYYY-MM-DD strings). Defaults to today; user adjusts
  // via the two <input type="date"> controls revealed when range==="custom".
  const todayStr = isoDay(new Date());
  const [customFrom, setCustomFrom] = useState(todayStr);
  const [customTo, setCustomTo] = useState(todayStr);
  const storedId =
    typeof window !== "undefined" ? localStorage.getItem("verifierid") : null;
  const [verifierId] = useState(storedId || "");

  const [data, setData] = useState(null);
  const [teamData, setTeamData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Patient drill-down modal state
  const [drillDown, setDrillDown] = useState(null); // { verifierId, name, date }
  const [drillData, setDrillData] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillError, setDrillError] = useState(null);

  const fetchData = useCallback(async () => {
    // For custom range, both dates must be set and valid.
    if (range === "custom") {
      if (!customFrom || !customTo) return;
      if (customFrom > customTo) {
        setError(new Error("'From' date must be on or before 'To' date."));
        setLoading(false);
        return;
      }
    }
    setLoading(true);
    setError(null);
    try {
      if (scope === "team") {
        const qs =
          range === "custom"
            ? `from=${customFrom}&to=${customTo}`
            : `range=${range}`;
        const url = `${apiUrl}api/users/verifierAttendance/list?${qs}`;
        const res = await axios.get(url);
        setTeamData(res.data);
      } else {
        if (!verifierId) {
          setError(new Error("No verifier ID in session."));
          setLoading(false);
          return;
        }
        const url =
          range === "custom"
            ? `${apiUrl}api/users/verifierAttendance/range` +
              `?verifierId=${encodeURIComponent(verifierId)}` +
              `&from=${customFrom}&to=${customTo}`
            : `${apiUrl}api/users/verifierAttendance/${range}` +
              `?verifierId=${encodeURIComponent(verifierId)}`;
        const res = await axios.get(url);
        setData(res.data);
      }
    } catch (err) {
      console.error("[AttendanceView] fetch failed:", err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [range, scope, verifierId, customFrom, customTo]);

  useEffect(() => {
    fetchData();
    // Refresh on tab focus instead of polling — keeps data fresh after a
    // logout/login round-trip without burning requests when the tab is idle.
    const onFocus = () => fetchData();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [fetchData]);

  // If embedded on a profile page, never let the view drift into team scope.
  useEffect(() => {
    if (selfOnly && scope !== "me") setScope("me");
  }, [selfOnly, scope]);

  // Fetch the per-patient list whenever the drill-down modal opens.
  useEffect(() => {
    if (!drillDown) return;
    let cancelled = false;
    setDrillLoading(true);
    setDrillError(null);
    setDrillData(null);
    const url =
      `${apiUrl}api/users/verifierAttendance/patients` +
      `?verifierId=${encodeURIComponent(drillDown.verifierId)}` +
      (drillDown.date ? `&date=${encodeURIComponent(drillDown.date)}` : "");
    axios
      .get(url)
      .then((res) => {
        if (!cancelled) setDrillData(res.data);
      })
      .catch((err) => {
        if (!cancelled) setDrillError(err);
      })
      .finally(() => {
        if (!cancelled) setDrillLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [drillDown]);

  const openVerifierDrill = (verifier) => {
    setDrillDown({
      verifierId: verifier.verifierId || verifier._id,
      name: verifier.name,
      // For team view "today" we drill into today; for week/month we omit
      // date so the backend returns the full-range patient list.
      date: range === "today" ? new Date().toISOString().slice(0, 10) : null,
    });
  };

  // Normalize the response into a uniform shape the rendering code can
  // consume, regardless of whether we're on the today/week/month endpoint.
  const view = useMemo(() => {
    if (!data) return null;
    if (range === "today") {
      const d = data.day || {};
      // WorkedMinutes is now incremented on every heartbeat by the backend
      // (with gap detection — closed-tab time is excluded), so we display
      // it directly. No client-side computation needed.
      return {
        days: d.FirstLoginAt ? [d] : [],
        totals: {
          ...d,
          DaysPresent: d.FirstLoginAt ? 1 : 0,
          WorkedMinutes: d.WorkedMinutes || 0,
          BreakMinutes: Math.max(0, d.BreakMinutes || 0),
          ActivityAvg: Math.max(0, Math.min(100, d.ActivityAvg || 0)),
        },
        rangeStart: d.DayDate || null,
        rangeEnd: d.DayDate || null,
      };
    }
    return {
      days: Array.isArray(data.days) ? data.days : [],
      totals: data.totals || {},
      rangeStart: data.range && data.range.start,
      rangeEnd: data.range && data.range.end,
    };
  }, [data, range]);

  // Build a CSV from whatever the user is currently looking at — same range
  // tab + same scope. For team view we export one row per verifier; for
  // single-verifier view we export one row per day in the range.
  const handleExportCsv = () => {
    const stamp = new Date().toISOString().slice(0, 10);
    if (scope === "team") {
      const verifiers = (teamData && teamData.verifiers) || [];
      const header = [
        "Verifier",
        "Days Present",
        "Activity %",
        "Files Verified",
        "Patients Verified",
        "Verified Duration (mins)",
        "Verified Duration",
        "Avg per file (sec)",
        "Avg per file",
        "Median per file (sec)",
        "Verify intervals",
        "First Login",
        "Last Logout",
        "Login Hours (mins)",
        "Login Hours",
        "Worked (mins)",
        "Worked",
        "Break (mins)",
        "Break",
      ];
      const body = verifiers.map((v) => [
        v.name || "Unknown",
        v.DaysPresent || 0,
        Math.round(v.ActivityAvg || 0),
        v.FilesVerified || 0,
        v.PatientsVerified || 0,
        Math.round(v.VerifiedMinutes || 0),
        fmtVerifiedDuration(v.VerifiedMinutes || 0),
        Math.round(v.AvgVerifySeconds || 0),
        fmtVerifySpeed(v.AvgVerifySeconds),
        Math.round(v.MedianVerifySeconds || 0),
        v.VerifyIntervalCount || 0,
        fmtTime(v.FirstLoginAt),
        fmtTime(v.LastLogoutAt),
        Math.round(v.LoginMinutes || 0),
        fmtDuration(v.LoginMinutes || 0),
        Math.round(v.WorkedMinutes || 0),
        fmtDuration(v.WorkedMinutes || 0),
        Math.round(v.BreakMinutes || 0),
        fmtDuration(v.BreakMinutes || 0),
      ]);
      downloadCsv(
        [header, ...body],
        `attendance-team-${range}-${stamp}.csv`
      );
    } else {
      const days = (view && view.days) || [];
      const header = [
        "Date",
        "Files",
        "Patients",
        "Verified Duration (mins)",
        "Verified Duration",
        "Avg per file (sec)",
        "Avg per file",
        "Median per file (sec)",
        "Verify intervals",
        "First Login",
        "Last Logout",
        "Login Hours (mins)",
        "Login Hours",
        "Worked (mins)",
        "Worked",
        "Break (mins)",
        "Break",
        "Activity %",
        "Status",
      ];
      const body = days.map((d) => [
        fmtDate(d.DayDate),
        d.FilesVerified || 0,
        d.PatientsVerified || 0,
        Math.round(d.VerifiedMinutes || 0),
        fmtVerifiedDuration(d.VerifiedMinutes || 0),
        Math.round(d.AvgVerifySeconds || 0),
        fmtVerifySpeed(d.AvgVerifySeconds),
        Math.round(d.MedianVerifySeconds || 0),
        d.VerifyIntervalCount || 0,
        fmtTime(d.FirstLoginAt),
        fmtTime(d.LastLogoutAt),
        Math.round(d.LoginMinutes || 0),
        fmtDuration(d.LoginMinutes || 0),
        Math.round(d.WorkedMinutes || 0),
        fmtDuration(d.WorkedMinutes || 0),
        Math.round(d.BreakMinutes || 0),
        fmtDuration(d.BreakMinutes || 0),
        Math.round(d.ActivityAvg || 0),
        d.LastLogoutAt ? "Closed" : "Active",
      ]);
      downloadCsv(
        [header, ...body],
        `attendance-me-${range}-${stamp}.csv`
      );
    }
  };

  const exportDisabled =
    scope === "team"
      ? !teamData || !teamData.verifiers || teamData.verifiers.length === 0
      : !view || !view.days || view.days.length === 0;

  const showScopeBar = (
    <div className="att-controls">
      <div className="att-controls__left">
        <div className="att-tabs" role="tablist">
          {RANGES.map((r) => (
            <button
              key={r.key}
              role="tab"
              aria-selected={range === r.key}
              className={"att-tab " + (range === r.key ? "is-active" : "")}
              onClick={() => setRange(r.key)}
              type="button"
            >
              {r.label}
            </button>
          ))}
        </div>
        {range === "custom" && (
          <div className="att-daterange">
            <label className="att-daterange__field">
              <span>From</span>
              <input
                type="date"
                value={customFrom}
                max={customTo || undefined}
                onChange={(e) => setCustomFrom(e.target.value)}
              />
            </label>
            <label className="att-daterange__field">
              <span>To</span>
              <input
                type="date"
                value={customTo}
                min={customFrom || undefined}
                max={isoDay(new Date())}
                onChange={(e) => setCustomTo(e.target.value)}
              />
            </label>
          </div>
        )}
      </div>
      <div className="att-controls__right">
        {!selfOnly && (
          <div className="att-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={scope === "me"}
              className={"att-tab " + (scope === "me" ? "is-active" : "")}
              onClick={() => setScope("me")}
            >
              My attendance
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={scope === "team"}
              className={"att-tab " + (scope === "team" ? "is-active" : "")}
              onClick={() => setScope("team")}
            >
              All verifiers
            </button>
          </div>
        )}
        <button
          type="button"
          className="att-download"
          onClick={handleExportCsv}
          disabled={exportDisabled}
          title={
            exportDisabled
              ? "No data in the current selection"
              : "Download the current view as CSV"
          }
        >
          Download CSV
        </button>
      </div>
    </div>
  );

  if (loading && !data && !teamData) {
    return (
      <div className="att-view">
        {showScopeBar}
        <div className="att-state">Loading attendance…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="att-view">
        {showScopeBar}
        <div className="att-state att-state--error">
          Could not load attendance data. Please try again.
        </div>
      </div>
    );
  }

  // Patient drill-down modal — same markup is reused from both the team
  // and (later) single-verifier views.
  const drillModal = drillDown ? (
    <div
      className="att-modal__backdrop"
      role="dialog"
      aria-modal="true"
      onClick={() => setDrillDown(null)}
    >
      <div className="att-modal" onClick={(e) => e.stopPropagation()}>
        <div className="att-modal__head">
          <div>
            <h3>Patients verified</h3>
            <span className="att-modal__sub">
              {drillDown.name}
              {drillDown.date ? ` — ${drillDown.date}` : ""}
            </span>
          </div>
          <button
            type="button"
            className="att-modal__close"
            onClick={() => setDrillDown(null)}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="att-modal__body">
          {drillLoading && <div className="att-state">Loading patients…</div>}
          {drillError && (
            <div className="att-state att-state--error">
              Could not load patient list.
            </div>
          )}
          {!drillLoading && !drillError && drillData && (
            (drillData.patients || []).length === 0 ? (
              <div className="att-empty">No patients verified for this verifier.</div>
            ) : (
              <table className="att-table">
                <thead>
                  <tr>
                    <th>Patient</th>
                    <th className="att-col-tight">Files</th>
                    <th className="att-col-num">Duration</th>
                    <th className="att-col-num">Last verified</th>
                  </tr>
                </thead>
                <tbody>
                  {drillData.patients.map((p) => (
                    <tr key={p.patientId}>
                      <td>{p.name || p.patientId}</td>
                      <td className="att-col-tight">{p.fileCount || 0}</td>
                      <td className="att-col-num">
                        {fmtVerifiedDuration(p.VerifiedMinutes || 0)}
                      </td>
                      <td className="att-col-num">
                        {p.lastVerifiedAt ? fmtTime(p.lastVerifiedAt) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="att-modal__totals">
                    <td>Total ({drillData.patients.length})</td>
                    <td className="att-col-tight">
                      {drillData.patients.reduce(
                        (s, p) => s + (p.fileCount || 0),
                        0
                      )}
                    </td>
                    <td className="att-col-num">
                      {fmtVerifiedDuration(
                        drillData.patients.reduce(
                          (s, p) => s + (p.VerifiedMinutes || 0),
                          0
                        )
                      )}
                    </td>
                    <td className="att-col-num">—</td>
                  </tr>
                </tfoot>
              </table>
            )
          )}
        </div>
      </div>
    </div>
  ) : null;

  // ─── Team view: one row per verifier, aggregated for the chosen range ──
  if (scope === "team") {
    const verifiers = (teamData && teamData.verifiers) || [];
    return (
      <div className="att-view">
        {showScopeBar}
        {drillModal}
        <div className="att-table-card">
          <div className="att-table-card__head">
            <h3>
              All verifiers — {RANGES.find((r) => r.key === range).label}
            </h3>
            <span className="att-chart-card__hint">
              {verifiers.length} {verifiers.length === 1 ? "verifier" : "verifiers"}
            </span>
          </div>
          <div className="att-table-wrap">
            <table className="att-table">
              <thead>
                <tr>
                  <th>Verifier</th>
                  <th className="att-col-tight">Days</th>
                  <th className="att-col-tight">Activity</th>
                  <th className="att-col-tight">Files</th>
                  <th className="att-col-tight">Patients</th>
                  <th className="att-col-num">Verified</th>
                  <th className="att-col-num">Avg/file</th>
                  <th className="att-col-num">First login</th>
                  <th className="att-col-num">Last logout</th>
                  <th className="att-col-num">Login hrs</th>
                  <th className="att-col-num">Worked</th>
                  <th className="att-col-num">Break</th>
                </tr>
              </thead>
              <tbody>
                {verifiers.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="att-empty">
                      No attendance recorded for this range.
                    </td>
                  </tr>
                ) : (
                  verifiers.map((v) => (
                    <tr key={v.verifierId}>
                      <td>
                        <button
                          type="button"
                          className="att-link"
                          onClick={() => openVerifierDrill(v)}
                          title="View patients verified"
                        >
                          {v.name || "—"}
                        </button>
                      </td>
                      <td className="att-col-tight">{v.DaysPresent || 0}</td>
                      <td className="att-col-tight">
                        <ActivityText value={v.ActivityAvg || 0} />
                      </td>
                      <td className="att-col-tight">{v.FilesVerified || 0}</td>
                      <td className="att-col-tight">
                        {v.PatientsVerified || 0}
                      </td>
                      <td className="att-col-num">
                        {fmtVerifiedDuration(v.VerifiedMinutes || 0)}
                      </td>
                      <td
                        className="att-col-num"
                        title={
                          v.VerifyIntervalCount
                            ? `Median ${fmtVerifySpeed(v.MedianVerifySeconds)} · ${v.VerifyIntervalCount} intervals`
                            : "Not enough verifications to compute speed"
                        }
                      >
                        {fmtVerifySpeed(v.AvgVerifySeconds)}
                      </td>
                      <td className="att-col-num">{fmtTime(v.FirstLoginAt)}</td>
                      <td className="att-col-num">{fmtTime(v.LastLogoutAt)}</td>
                      <td className="att-col-num">
                        {fmtDuration(v.LoginMinutes || 0)}
                      </td>
                      <td className="att-col-num">
                        {fmtDuration(v.WorkedMinutes || 0)}
                      </td>
                      <td className="att-col-num">
                        {fmtDuration(v.BreakMinutes || 0)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  if (!view) return null;

  const { days, totals } = view;
  const firstLogin = days.length ? days[0].FirstLoginAt : null;
  const lastLogout = days.reduce(
    (acc, d) =>
      d.LastLogoutAt && (!acc || new Date(d.LastLogoutAt) > new Date(acc))
        ? d.LastLogoutAt
        : acc,
    null
  );

  return (
    <div className="att-view">
      {showScopeBar}
      {drillModal}

      <div className="att-kpis">
        <Kpi
          label={range === "today" ? "Status" : "Days present"}
          value={
            range === "today"
              ? days.length > 0
                ? "Present"
                : "Absent"
              : totals.DaysPresent || 0
          }
          sub={
            range === "month" && totals.WorkingDays
              ? `of ${totals.WorkingDays} working days`
              : null
          }
        />
        <Kpi label="First login" value={fmtTime(firstLogin)} />
        <Kpi
          label={range === "today" ? "Last activity" : "Last logout"}
          value={fmtTime(lastLogout)}
        />
        <Kpi label="Login hrs" value={fmtDuration(totals.LoginMinutes || 0)} />
        <Kpi label="Worked" value={fmtDuration(totals.WorkedMinutes || 0)} />
        <Kpi label="Break" value={fmtDuration(totals.BreakMinutes || 0)} />
        <Kpi
          label="Activity"
          value={`${totals.ActivityAvg || 0}%`}
          sub={range === "today" ? null : "average"}
          tone={activityTierClass(totals.ActivityAvg || 0).replace(
            "att-activity--",
            ""
          )}
        />
        <Kpi label="Files" value={totals.FilesVerified || 0} />
        <Kpi label="Patients" value={totals.PatientsVerified || 0} />
        <Kpi
          label="Verified"
          value={fmtVerifiedDuration(totals.VerifiedMinutes || 0)}
          sub="ECG duration"
        />
        <Kpi
          label="Avg / file"
          value={fmtVerifySpeed(totals.AvgVerifySeconds)}
          sub={
            totals.VerifyIntervalCount
              ? `${totals.VerifyIntervalCount} intervals`
              : null
          }
        />
        {range === "month" && (
          <Kpi
            label="Attendance"
            value={`${totals.AttendancePct || 0}%`}
          />
        )}
      </div>

      {range !== "today" && days.length > 0 && (
        <div className="att-charts">
          <div className="att-chart-card">
            <div className="att-chart-card__head">
              <h3>Worked hours per day</h3>
              <span className="att-chart-card__hint">
                {fmtDate(view.rangeStart)} – {fmtDate(view.rangeEnd)}
              </span>
            </div>
            <MiniBars
              days={days}
              valueKey="WorkedMinutes"
              fillVariant="worked"
              formatValue={(v) => fmtDuration(v)}
              ariaLabel="Worked minutes per day"
            />
          </div>
          <div className="att-chart-card">
            <div className="att-chart-card__head">
              <h3>Activity per day</h3>
              <span className="att-chart-card__hint">
                avg {view.totals.ActivityAvg || 0}%
              </span>
            </div>
            <MiniBars
              days={days}
              valueKey="ActivityAvg"
              fixedMax={100}
              fillVariant="activity"
              formatValue={(v) => `${Math.round(v)}%`}
              ariaLabel="Activity percentage per day"
            />
          </div>
        </div>
      )}

      <div className="att-table-card">
        <div className="att-table-card__head">
          <h3>{range === "today" ? "Today's session" : "Daily attendance"}</h3>
        </div>
        <div className="att-table-wrap">
          <table className="att-table">
            <thead>
              <tr>
                <th>Date</th>
                <th className="att-col-tight">Files</th>
                <th className="att-col-tight">Patients</th>
                <th className="att-col-num">Verified</th>
                <th className="att-col-num">Avg/file</th>
                <th className="att-col-num">First Login</th>
                <th className="att-col-num">Last Logout</th>
                <th className="att-col-num">Login hrs</th>
                <th className="att-col-num">Worked</th>
                <th className="att-col-num">Break</th>
                <th className="att-col-activity">Activity</th>
                <th className="att-col-status">Status</th>
              </tr>
            </thead>
            <tbody>
              {days.length === 0 ? (
                <tr>
                  <td colSpan={12} className="att-empty">
                    No attendance recorded for this range.
                  </td>
                </tr>
              ) : (
                days.map((d) => (
                  <tr key={d.DayDate || d._id}>
                    <td>{fmtDate(d.DayDate)}</td>
                    <td className="att-col-tight">{d.FilesVerified || 0}</td>
                    <td className="att-col-tight">{d.PatientsVerified || 0}</td>
                    <td className="att-col-num">
                      {fmtVerifiedDuration(d.VerifiedMinutes || 0)}
                    </td>
                    <td
                      className="att-col-num"
                      title={
                        d.VerifyIntervalCount
                          ? `Median ${fmtVerifySpeed(d.MedianVerifySeconds)} · ${d.VerifyIntervalCount} intervals`
                          : "Not enough verifications to compute speed"
                      }
                    >
                      {fmtVerifySpeed(d.AvgVerifySeconds)}
                    </td>
                    <td className="att-col-num">{fmtTime(d.FirstLoginAt)}</td>
                    <td className="att-col-num">{fmtTime(d.LastLogoutAt)}</td>
                    <td className="att-col-num">
                      {fmtDuration(d.LoginMinutes || 0)}
                    </td>
                    <td className="att-col-num">
                      {fmtDuration(d.WorkedMinutes || 0)}
                    </td>
                    <td className="att-col-num">
                      {fmtDuration(d.BreakMinutes || 0)}
                    </td>
                    <td className="att-col-activity">
                      <ActivityBar value={d.ActivityAvg || 0} />
                    </td>
                    <td className="att-col-status">
                      <span
                        className={
                          "att-pill att-pill--" +
                          (d.LastLogoutAt ? "ok" : "open")
                        }
                      >
                        {d.LastLogoutAt ? "Closed" : "Active"}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AttendanceView;
